import { useAddParticipantsToCategory } from '@pages/event/api/useAddParticipantsToCategory';
import { useRemoveTicketsFromCategory } from '@pages/event/api/useFetchDeleteTicketCategory';
import { useGetParticipantsByEventId } from '@pages/event/api/useGetParticipantsByEventId';
import { ErrorIcon, SuccessIcon } from '@shared/assets/icons';
import { Category } from '@pages/event/api/useFetchCategory';
import { InvitedParticipants } from '@widgets/segments-tab';
import { Checkbox, PreloaderCircular } from '@shared/ui/kit';
import { concatArray, pluralizePhrase } from '@shared/lib';
import { FilterParticipants } from './FilterParticipants';
import { SOURCE_CB } from '@shared/constants/sourceData';
import { participantForms } from '@entities/clients';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { Modal } from '@shared/ui/modal';
import { styles } from './styles';

const STATUSES_CREATE_EMPLOYEE = {
  default: '',
  success: 'success',
  error: 'error',
};

interface AddParticipantsCBToCategoryModalProps {
  isOpen: boolean;
  refetchParticipants: (total?: number) => void;
  invitedParticipants?: InvitedParticipants[];
  onClose?: () => void;
  category?: Category;
  handleCreateCategory?: () => Promise<Category | undefined>;
}

export const AddParticipantsCBToCategoryModal = ({
  isOpen,
  onClose,
  invitedParticipants = [],
  refetchParticipants,
  category,
  handleCreateCategory,
}: AddParticipantsCBToCategoryModalProps) => {
  const { id } = useParams();
  const { participants, fetching, refetch, handleSearchChange, handleGuestFilterChange } = useGetParticipantsByEventId({
    eventId: Number(id),
    source: SOURCE_CB,
  });

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const isIndeterminate = participants?.some(({ id }) => selectedIds.has(id));
  const isAllChecked = participants?.every(({ id }) => selectedIds.has(id));
  const [, addParticipantsToCategory] = useAddParticipantsToCategory();
  const [, removeTicketsFromCategory] = useRemoveTicketsFromCategory();
  const [countTicketsToDelete, setCountTicketsToDelete] = useState<number>(0);
  const [countTicketsToAdd, setCountTicketsToAdd] = useState<number>(0);
  const [statusCreate, setStatusCreate] = useState(STATUSES_CREATE_EMPLOYEE.default);
  const { id: categoryId, title } = category || {};

  const onCheckboxClick = (index: number) => {
    if (selectedIds.has(index)) {
      selectedIds.delete(index);
    } else {
      selectedIds.add(index);
    }

    const newIds = new Set(selectedIds);
    setSelectedIds(newIds);
  };

  const onCheckAllClick = () => {
    if (isAllChecked) {
      participants?.forEach(({ id }) => selectedIds.delete(id));

      const newIds = new Set(selectedIds);
      setSelectedIds(newIds);
      return;
    }

    const newIds = new Set([...selectedIds, ...(participants?.map(({ id }) => id) || [])]);
    setSelectedIds(newIds);
  };

  const onSaveParticipants = async () => {
    try {
      const participantIdsAdd = new Set(selectedIds);
      const ticketIdsToDelete = [];
      setCountTicketsToAdd(0);
      setCountTicketsToDelete(0);

      for (const { userId, ticketId } of invitedParticipants) {
        participantIdsAdd.delete(userId);
        if (selectedIds.has(userId)) continue;
        ticketIdsToDelete.push(ticketId);
      }

      if (!categoryId) {
        category = await handleCreateCategory?.();
      }

      if (ticketIdsToDelete.length) {
        const { error } = await removeTicketsFromCategory({
          input: { categoryId: category?.id || 0, ticketIds: ticketIdsToDelete },
        });

        setCountTicketsToDelete(ticketIdsToDelete.length);

        if (error) {
          setStatusCreate(STATUSES_CREATE_EMPLOYEE.error);
          return;
        }
      }

      if (participantIdsAdd.size) {
        const { error } = await addParticipantsToCategory({
          categoryId: category?.id || 0,
          employeeIds: Array.from(participantIdsAdd),
          guestsData: [],
        });

        setCountTicketsToAdd(participantIdsAdd.size);

        if (error) {
          setStatusCreate(STATUSES_CREATE_EMPLOYEE.error);
          return;
        }
      }

      if (!handleCreateCategory) {
        setStatusCreate(STATUSES_CREATE_EMPLOYEE.success);
        return;
      }

      refetchParticipants(participantIdsAdd.size);
      onClose?.();
    } catch (err) {
      setStatusCreate(STATUSES_CREATE_EMPLOYEE.error);
    }
  };

  const handleClose = () => {
    setStatusCreate(STATUSES_CREATE_EMPLOYEE.default);
    refetchParticipants(countTicketsToAdd);
    onClose?.();
  };

  useEffect(() => {
    if (!isOpen) return;
    refetch();

    const ids = invitedParticipants?.map(({ userId }) => userId);
    setSelectedIds(new Set(ids));
  }, [isOpen]);

  const getTextStatusCreate = () => {
    if (statusCreate === STATUSES_CREATE_EMPLOYEE.error) {
      return 'Во время добавления участников, произошла непредвиденная ошибка. Повторите попытку позднее';
    }

    const addedText = countTicketsToAdd
      ? `добавлено: ${countTicketsToAdd} ${pluralizePhrase(countTicketsToAdd, participantForms)}`
      : '';

    const deletedText = countTicketsToDelete
      ? `удалено: ${countTicketsToDelete} ${pluralizePhrase(countTicketsToDelete, participantForms)}`
      : '';

    const text = concatArray([addedText, deletedText], ', ');

    if (!text) return `В категорию «${title}» участники не были добавлены`;

    return `${addedText ? 'В категорию' : 'Из категории'} «${title}» успешно ${text}`;
  };

  if (statusCreate) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title="Добавление участников"
        width={460}
        applyButton={{ text: 'Закрыть', onClick: handleClose }}
        headerClassName={stylex.attrs(styles.modalStatusHeader).class}
        {...stylex.props(styles.modalStatus)}
      >
        <div {...stylex.props(styles.modalStatusContent)}>
          <div {...stylex.props(styles.icon)}>
            {statusCreate === STATUSES_CREATE_EMPLOYEE.success ? <SuccessIcon /> : <ErrorIcon />}
          </div>

          {getTextStatusCreate()}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Добавить участников в категорию"
      width={920}
      cancelButton={{ text: categoryId ? 'Отменить' : 'Пропустить', onClick: onClose }}
      applyButton={{ text: 'Добавить', onClick: onSaveParticipants }}
      headerClassName={stylex.attrs(styles.modalHeader).class}
      {...stylex.props(styles.modal)}
    >
      <div {...stylex.props(styles.container)}>
        <FilterParticipants handleSearchChange={handleSearchChange} handleGuestFilterChange={handleGuestFilterChange} />

        {!fetching && !!participants?.length && (
          <div {...stylex.props(styles.participants)}>
            <div {...stylex.props(styles.participant, !isAllChecked && isIndeterminate && styles.checkAllButton)}>
              <Checkbox onChange={onCheckAllClick} isChecked={isAllChecked} isIndeterminate={isIndeterminate} />
              Выбрать всех участников
              <span {...stylex.props(styles.counter)}>
                Выбрано: {selectedIds.size} {pluralizePhrase(selectedIds.size, participantForms)}
              </span>
            </div>

            {participants.map((participant) => (
              <div {...stylex.props(styles.participant)} key={participant.id}>
                <Checkbox
                  onChange={() => onCheckboxClick(participant.id)}
                  isChecked={selectedIds.has(participant.id)}
                />

                {concatArray([participant.lastName, participant.firstName, participant.surname])}
              </div>
            ))}
          </div>
        )}

        {!participants?.length && !fetching && <span {...stylex.props(styles.notFound)}>Ничего не найдено</span>}

        {fetching && (
          <div {...stylex.props(styles.loader)}>
            <PreloaderCircular />
          </div>
        )}
      </div>
    </Modal>
  );
};
